/**
 * Quick View Modal Accessibility
 *
 * Main improvements:
 * - moves initial focus into the Quick View modal;
 * - identifies Quick View as a modal dialog;
 * - traps keyboard focus within the open modal;
 * - attempts to restore focus to the control that opened the modal.
 *
 * Note:
 * A third-party accessibility widget may override focus restoration
 * after the modal closes.
 */

(function () {
  const focusableSelector = [
    "a[href]",
    "button:not([disabled])",
    'input:not([disabled]):not([type="hidden"])',
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");

  let quickViewTrigger = null;
  let wasQuickViewOpen = false;

  function getQuickViewModal() {
    const quickView = Array.from(
      document.querySelectorAll(".m-product-quickview"),
    ).find(window.ASA11y.isVisible);

    if (!quickView) return null;

    return quickView.closest(".m-modal");
  }

  function getFocusable(modal) {
    if (!modal) return [];

    return Array.from(modal.querySelectorAll(focusableSelector)).filter(
      function (element) {
        return (
          element.tabIndex >= 0 &&
          window.ASA11y.isVisible(element) &&
          !element.closest('[aria-hidden="true"]') &&
          !element.closest("[inert]")
        );
      },
    );
  }

  /**
   * Moves initial focus to the Close button or the first available
   * interactive control in the Quick View modal.
   */
  function moveFocusIntoQuickView() {
    const modal = getQuickViewModal();

    if (!modal) return;

    wasQuickViewOpen = true;

    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");

    const quickView = modal.querySelector(".m-product-quickview");
    const title = quickView?.querySelector(".m-product-title[id]");

    if (title) {
      modal.setAttribute("aria-labelledby", title.id);
    }

    const closeButton = modal.querySelector(".m-modal--close");
    const focusable = getFocusable(modal);

    const target =
      closeButton && window.ASA11y.isVisible(closeButton)
        ? closeButton
        : focusable[0];

    if (target) {
      target.focus({
        preventScroll: true,
      });
    }
  }

  /**
   * Returns focus to the Quick View button after the modal closes.
   */
  function restoreQuickViewTriggerFocus() {
    const trigger = quickViewTrigger;

    quickViewTrigger = null;

    if (
      !trigger ||
      !document.contains(trigger) ||
      !window.ASA11y.isVisible(trigger)
    ) {
      return;
    }

    requestAnimationFrame(function () {
      trigger.focus({
        preventScroll: true,
      });
    });
  }

  /**
   * Traps Tab and Shift+Tab navigation inside the open Quick View modal.
   */
  function initQuickViewFocusTrap() {
    document.addEventListener(
      "keydown",
      function (event) {
        if (event.key !== "Tab") return;

        const modal = getQuickViewModal();

        if (!modal) return;

        const focusable = getFocusable(modal);

        if (!focusable.length) {
          event.preventDefault();
          return;
        }

        const active = document.activeElement;
        const currentIndex = focusable.indexOf(active);

        let nextIndex;

        if (event.shiftKey) {
          nextIndex =
            currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;
        } else {
          nextIndex =
            currentIndex === -1 || currentIndex >= focusable.length - 1
              ? 0
              : currentIndex + 1;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        focusable[nextIndex].focus({
          preventScroll: true,
        });
      },
      true,
    );
  }

  /**
   * Saves the element that opened Quick View and moves focus
   * into the modal after its asynchronous rendering is complete.
   */
  function initQuickViewTriggerTracking() {
    document.addEventListener("click", function (event) {
      const trigger = event.target.closest(
        [
          "[data-open-quickview]",
          "[data-open-quick-view]",
          "[data-quickview]",
          "[data-quick-view]",
          ".m-product-card__quickview",
          ".m-button--quickview",
        ].join(","),
      );

      if (!trigger) return;

      quickViewTrigger = trigger;

      window.setTimeout(moveFocusIntoQuickView, 400);
    });
  }

  /**
   * Detects when Quick View closes and restores focus to its trigger.
   */
  function initQuickViewCloseObserver() {
    const quickViewObserver = new MutationObserver(function () {
      const modal = getQuickViewModal();
      const isOpen = Boolean(modal && window.ASA11y.isVisible(modal));

      if (isOpen) {
        wasQuickViewOpen = true;
        return;
      }

      if (wasQuickViewOpen) {
        wasQuickViewOpen = false;
        restoreQuickViewTriggerFocus();
      }
    });

    quickViewObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "aria-hidden"],
    });
  }

  function initQuickViewAccessibility() {
    initQuickViewFocusTrap();
    initQuickViewTriggerTracking();
    initQuickViewCloseObserver();
  }

  window.ASA11y.onReady(initQuickViewAccessibility);
})();
